// Card 70, fairways (R2717, R2723; ui-map-interface.md §17.3.70,
// v2-the-deep.md §7.1; mockup round 115 cards 02 and 07).
//
// A fairway is a corridor of water through a strategic narrows, and buoys
// mark it. The map generator makes both with no draw, and the hello carries
// them while the flag `routes` is latched (`Hello.fairways`,
// `Hello.buoys`). The piece reads no flag: a world with the flag off sends
// no fairway, and the chart of today stands.
//
// **ONE SET, TWO READINGS.** The owner picked one set of round 115 for the
// fairway, the guts (card 50), the shipping lane (card 16) and the Fanal,
// and the chart reading picks the card. The modern reading draws
// card 02 Glow Channel: a broad soft glow with no edge, and each buoy a
// white pip in its own halo, cut to its shape. The historical reading draws
// card 07 Engraved Survey: no line, italic soundings along each chain, the
// name of the narrows, and each buoy the engraved side view on a waterline.
// A fairway has no owner, so it never takes a faction colour: the pale
// ink against the faction ink of the lane.
//
// **THE SOUNDINGS READ THE BAND.** A figure along a chain is the sounding of
// card 02 at its cell (`soundingMetres`), so the chart tells one depth
// story; a cell with no band (the flag depth off, or water the survey does
// not hold) writes none.
//
// **COST.** The fairways are of the map, so the pack is static: it packs on
// a hello, a reading, a zoom band (the sounding spacing) and a survey
// change. A buoy is two quads from the atlas (§10).

import { CHART, MARKER_NEUTRAL } from '../../../gen/presentation';
import { chart as S } from '../../../gen/strings';
import { BuoyKind, type Fairway } from '../../../gen/proto/aquaphobia';
import { cellAt, cellCenterX, cellCenterY, mpxToPx } from '../../../world';
import { BUOY_SHAPES, GLOW_CELL_R, GLOW_KEY, buoyCellKey, type BuoyShape } from '../../../render/fairway-cells';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { CHART_INK, faceOf, inkOf } from './shared';
import { soundingMetres } from './soundings';

const T = CHART.pieces.fairways;

/** A point in world px. */
type P = readonly [number, number];

/** One drawn buoy: its shape and its point. */
export interface DrawnBuoy { kind: BuoyShape; x: number; y: number }

/** One figure or word the historical reading writes. */
export interface ChartWord { x: number; y: number; text: string }

/** The shape of a wire kind, or null for none. */
export function buoyShapeOf(kind: BuoyKind): BuoyShape | null {
  return kind >= BuoyKind.BUOY_KIND_PORT && kind <= BuoyKind.BUOY_KIND_JUNCTION ? BUOY_SHAPES[kind - 1] : null;
}

/**
 * Chaikin corner cuts on an open line, `passes` times. The two ends stay,
 * so a chain still meets its junction and its mouth.
 */
export function smoothOpen(pts: readonly P[], passes: number): P[] {
  let out: P[] = pts.slice();
  for (let n = 0; n < passes && out.length > 2; n++) {
    const next: P[] = [out[0]];
    for (let i = 0; i < out.length - 1; i++) {
      const [ax, ay] = out[i], [bx, by] = out[i + 1];
      if (i > 0) next.push([0.75 * ax + 0.25 * bx, 0.75 * ay + 0.25 * by]);
      if (i < out.length - 2) next.push([0.25 * ax + 0.75 * bx, 0.25 * ay + 0.75 * by]);
    }
    next.push(out[out.length - 1]);
    out = next;
  }
  return out;
}

/** The centres of the cells of a chain, smoothed. */
export function chainLine(cells: readonly number[], passes: number): P[] {
  return smoothOpen(cells.map((c) => [cellCenterX(c), cellCenterY(c)] as const), passes);
}

/**
 * Calls `fn` at every `step` px along a line from `phase`, with the point,
 * the unit tangent and the index of the sample.
 */
export function alongLine(pts: readonly P[], step: number, phase: number,
  fn: (x: number, y: number, tx: number, ty: number, k: number) => void): void {
  let s = phase, acc = 0, k = 0;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1], [bx, by] = pts[i];
    const len = Math.hypot(bx - ax, by - ay);
    if (len === 0) continue;
    while (s <= acc + len) {
      const u = (s - acc) / len;
      fn(ax + (bx - ax) * u, ay + (by - ay) * u, (bx - ax) / len, (by - ay) / len, k++);
      s += step;
    }
    acc += len;
  }
}

export class FairwaysPiece implements Piece {
  readonly id = 'fairways' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The fairways belong to the map of the season running now. */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello', 'reading', 'zoomband', 'font', 'survey'] as const;

  /** The smoothed line of every chain of the last pack. The tests read it. */
  lines: P[][] = [];
  /** The buoys of the last pack. */
  buoys: DrawnBuoy[] = [];
  /** The soundings and the names of the narrows of the last pack. */
  soundings: ChartWord[] = [];
  names: ChartWord[] = [];

  visible(ctx: ChartContext): boolean {
    return ctx.state.fairways.length > 0;
  }

  rebuild(ctx: ChartContext): void {
    const fairways: readonly Fairway[] = ctx.state.fairways;
    const passes = Math.max(0, Math.round(T.fig.smooth_passes));
    this.lines = fairways.flatMap((f) => f.chains.map((ch) => chainLine(ch.cells, passes)));
    this.buoys = [];
    for (const b of ctx.state.buoys) {
      const kind = buoyShapeOf(b.kind);
      if (kind) this.buoys.push({ kind, x: mpxToPx(b.xMpx), y: mpxToPx(b.yMpx) });
    }
    this.soundings = [];
    this.names = [];
    if (ctx.reading !== 'historical') return;
    const f = T.historical.fig;
    const depth = ctx.state.depthBands;
    const metres = depth ? soundingMetres(depth) : null;
    const step = ctx.zoom < 1 ? f.sounding_far_px : f.sounding_step_px;
    if (metres) {
      this.lines.forEach((line, ci) => alongLine(line, Math.max(1, step), f.sounding_phase_px, (x, y, tx, ty, k) => {
        const m = metres[cellAt(x, y)] ?? 0;
        if (m === 0) return;
        const off = ((k + ci) % 2 ? 1 : -1) * f.sounding_side_px;
        this.soundings.push({ x: x - ty * off, y: y + tx * off, text: String(m) });
      }));
    }
    for (const fw of fairways) {
      if (fw.narrows.length === 0) continue;
      const c = fw.narrows[fw.narrows.length >> 1];
      this.names.push({ x: cellCenterX(c), y: cellCenterY(c) + f.name_dy, text: S.fairway_narrows });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    if (ctx.reading === 'historical') this.emitHistorical(sinks, ctx);
    else this.emitModern(sinks, ctx);
  }

  /**
   * Card 02: the soft glow, and each buoy a pip in its halo. The glow is a
   * row of glow quads each `glow_step_px` along the line, so their sum is
   * one bell across the band: no stroke edge, no step in density, and a
   * corner as round as the line (R2821).
   */
  private emitModern(sinks: ChartSinks, ctx: ChartContext): void {
    const r = T.modern, f = r.fig;
    const pale = inkOf(r.ink, CHART_INK.pale);
    const glow = ctx.glyphs.mark(GLOW_KEY);
    const a = f.glow_alpha * r.alpha, s = f.glow_px / GLOW_CELL_R;
    for (const line of this.lines) {
      alongLine(line, Math.max(1, f.glow_step_px), 0, (x, y) => sinks.quads.sprite(x, y, 0, glow, pale, a, s));
    }
    for (const b of this.buoys) {
      sinks.quads.sprite(b.x, b.y, 0, glow, MARKER_NEUTRAL, f.buoy_glow_alpha, f.buoy_glow_px / GLOW_CELL_R);
    }
    for (const b of this.buoys) {
      sinks.quads.sprite(b.x, b.y, 0, ctx.glyphs.mark(buoyCellKey(false, b.kind)), MARKER_NEUTRAL, f.buoy_alpha, 1, 0, false);
    }
  }

  /** Card 07: the soundings, the name of the narrows and the engraved buoys. */
  private emitHistorical(sinks: ChartSinks, ctx: ChartContext): void {
    const r = T.historical, f = r.fig;
    const pale = inkOf(r.ink, CHART_INK.pale);
    const face = faceOf('historical');
    for (const s of this.soundings) {
      ctx.glyphs.word(sinks.quads, s.text, s.x, s.y, face, T.text.sounding, pale, r.alpha, { anchor: 'centre' });
    }
    for (const n of this.names) {
      ctx.glyphs.word(sinks.quads, n.text, n.x, n.y, face, T.text.name, pale, f.name_alpha, { anchor: 'centre' });
    }
    const h = f.waterline_half_px;
    for (const b of this.buoys) {
      sinks.segments.line(b.x - h, b.y, b.x + h, b.y, r.width, pale, f.waterline_alpha);
      sinks.quads.sprite(b.x, b.y, 0, ctx.glyphs.mark(buoyCellKey(true, b.kind)), pale, f.buoy_alpha, 1, 0, false);
    }
  }
}

/** The one instance the registry holds. */
export const fairways = new FairwaysPiece();
